import React, { Component } from "react";
import { hasBecomeTrue } from "~/hocs/crud";
import { messages } from "./../../i18n-messages";
import { injectIntl, InjectedIntlProps } from "@ai360/core/intl";
import { model, service } from "./../data";
import { PROPS_GROWTH_STAGE_GUID } from "./../../growth-stages/data/model";
import { getAgBytesErrorClassNames, onNumberChange, onTextChange } from "~/admin/utils";
// Components
import { TextInput, NumericInput, Section, SubSection, Checkbox, TextArea } from "~/core";
import { GrowthStageOrder, IAddEditPanelState, IAddEditProps } from "../interfaces";

export class AddEditPanel extends Component<IAddEditProps & InjectedIntlProps, IAddEditPanelState> {
    growthStageOrder: GrowthStageOrder;
    constructor(props) {
        super(props);
        this.growthStageOrder = props.record || {};
        this.state = {
            nextId: null,
        };
    }

    componentDidMount(): void {
        this.setupGrowthStageRecord();
        this.props.setBreadcrumbs([""]);
        const { needs, parentGuid, recordGuid } = this.props;
        needs([this.props.getNextId(parentGuid)]);

        if (this.props.recordGuid) {
            needs([this.props.fetchRecord(recordGuid)]);
        }
    }

    componentDidUpdate(prevProps: IAddEditProps & InjectedIntlProps): void {
        if (hasBecomeTrue("fetchData", prevProps, this.props)) {
            this.growthStageOrder[model.PROPS_GROWING_DEGREE_DAYS_LOW] = this.growthStageOrder[
                model.PROPS_GROWING_DEGREE_DAYS_LOW
            ]
                ? this.growthStageOrder[model.PROPS_GROWING_DEGREE_DAYS_LOW]
                : 0;
            this.growthStageOrder[model.PROPS_GROWING_DEGREE_DAYS_HIGH] = this.growthStageOrder[
                model.PROPS_GROWING_DEGREE_DAYS_HIGH
            ]
                ? this.growthStageOrder[model.PROPS_GROWING_DEGREE_DAYS_HIGH]
                : 0;
            this.props.liftRecordData(this.growthStageOrder);
        }

        if (this.props.addEditPanel.mode === "ADD") {
            this.setupNextId(this.props);
        } else {
            if (this.props.record && this.props.record !== prevProps.record) {
                this.growthStageOrder = this.props.record;
                this.setHeaderText();
            }
        }
    }

    setupNextId = (props: IAddEditProps & InjectedIntlProps): void => {
        if (props.nextId && props.nextId !== this.state.nextId) {
            this.growthStageOrder[model.PROPS_GROWTH_STAGE_ORDER] = "" + props.nextId;
            this.growthStageOrder[PROPS_GROWTH_STAGE_GUID] = "" + props.parentGuid;
            this.growthStageOrder[model.PROPS_GROWTH_STAGE_NAME] = "" + props.parentName;
            this.growthStageOrder[model.PROPS_GROWTH_STAGE_ID] = "" + props.parentId;
            this.setState({
                nextId: props.nextId,
            });
        }
    };

    setupGrowthStageRecord = (): void => {
        const { addEditPanel, record } = this.props;
        this.growthStageOrder = record || {};
        if (addEditPanel.mode === "ADD") {
            this.growthStageOrder = service.getDefaultRecord();
        }
    };

    onTextChange = (formKey: string, value: string | boolean, callback?: () => void): void => {
        this.growthStageOrder = onTextChange(
            this.growthStageOrder,
            { formKey: [formKey], value },
            callback
        );
    };

    onNumberChange = (formKey: string, value: string | number, callback?: () => void): void => {
        this.growthStageOrder = onNumberChange(
            this.growthStageOrder,
            { formKey: [formKey], value },
            callback
        );
    };

    setHeaderText = (): void => {
        this.props.setBreadcrumbs([this.growthStageOrder[model.PROPS_GROWTH_STAGE_ORDER_NAME]]);
    };

    renderGeneInfo = (): JSX.Element => {
        const { apiErrors, userRole } = this.props;
        const { formatMessage } = this.props.intl;
        const { growthStageOrder } = this;
        return (
            <div className="section-container">
                <Section testId="growth-stage-order-general-section">
                    <div className="form-sub-section section-column">
                        <span>Growth Stage Group ID: {this.props.parentId}</span>
                        <span>Growth Stage Group Name: {this.props.parentName}</span>
                    </div>
                    <SubSection testId="growth-stage-order-id-subsection">
                        <NumericInput
                            tabIndex={0}
                            scale={0}
                            precision={9}
                            containerClassNames={getAgBytesErrorClassNames([166, 167], apiErrors, [
                                "form-input-id",
                            ])}
                            suppressFormatting
                            placeholderText={formatMessage(messages.growthStageOrderID)}
                            value={"" + growthStageOrder[model.PROPS_GROWTH_STAGE_ORDER]}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_GROWTH_STAGE_ORDER, value)
                            }
                            required
                            testId="growth-stage-order-id-input"
                        />
                        {!userRole[model.PROPS_ACTIVE_INACTIVE] ? null : (
                            <Checkbox
                                className="active-checkbox"
                                onChange={(e, value) =>
                                    this.onTextChange(model.PROPS_ACTIVE_YN, value)
                                }
                                value={growthStageOrder[model.PROPS_ACTIVE_YN]}
                                label={formatMessage(messages.active)}
                                testId="growth-stage-order-is-active-checkbox"
                            />
                        )}
                    </SubSection>
                    <SubSection testId="growth-stage-order-name-and-description-subsection">
                        <TextInput
                            tabIndex={0}
                            maxLength={50}
                            containerClassNames={getAgBytesErrorClassNames([164, 165], apiErrors)}
                            placeholderText={formatMessage(messages.growthStageOrderName)}
                            value={growthStageOrder[model.PROPS_GROWTH_STAGE_ORDER_NAME]}
                            autoFocus
                            onChange={(value) =>
                                this.onTextChange(
                                    model.PROPS_GROWTH_STAGE_ORDER_NAME,
                                    value,
                                    this.setHeaderText
                                )
                            }
                            required
                            testId="growth-stage-order-name-input"
                        />
                        <TextInput
                            tabIndex={0}
                            containerClassNames={getAgBytesErrorClassNames(253, apiErrors)}
                            placeholderText={formatMessage(messages.description)}
                            value={growthStageOrder[model.PROPS_DESCRIPTION]}
                            onChange={(value) =>
                                this.onTextChange(
                                    model.PROPS_DESCRIPTION,
                                    value,
                                    this.setHeaderText
                                )
                            }
                            required
                            testId="growth-stage-order-description-input"
                        />
                    </SubSection>
                </Section>
            </div>
        );
    };

    render(): JSX.Element {
        const { apiErrors } = this.props;
        const { formatMessage } = this.props.intl;
        return (
            <div className="add-edit-panel">
                {this.renderGeneInfo()}
                <Section
                    headerText={formatMessage(messages.miscellaneousInfo)}
                    testId="growth-stage-order-miscellaneous-info-section"
                >
                    <SubSection testId="growth-stage-order-growing-degree-days-subsection">
                        <NumericInput
                            tabIndex={0}
                            scale={0}
                            precision={5}
                            containerClassNames={getAgBytesErrorClassNames(178, apiErrors)}
                            placeholderText={formatMessage(messages.growingDegreeDaysLowLabel)}
                            value={
                                this.growthStageOrder[model.PROPS_GROWING_DEGREE_DAYS_LOW] || "0"
                            }
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_GROWING_DEGREE_DAYS_LOW, value)
                            }
                            testId="growth-stage-order-growing-degree-days-low-input"
                        />
                        <NumericInput
                            tabIndex={0}
                            scale={0}
                            precision={5}
                            containerClassNames={getAgBytesErrorClassNames(178, apiErrors)}
                            placeholderText={formatMessage(messages.growingDegreeDaysHighLabel)}
                            value={
                                this.growthStageOrder[model.PROPS_GROWING_DEGREE_DAYS_HIGH] || "0"
                            }
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_GROWING_DEGREE_DAYS_HIGH, value)
                            }
                            testId="growth-stage-order-growing-degree-days-high-input"
                        />
                    </SubSection>
                    <SubSection testId="growth-stage-order-notes-subsection">
                        <TextArea
                            value={this.growthStageOrder[model.PROPS_GROWTH_STAGE_NOTES]}
                            onChange={(value) =>
                                this.onTextChange(model.PROPS_GROWTH_STAGE_NOTES, value)
                            }
                            placeholderText={formatMessage(messages.notes)}
                            testId="growth-stage-order-notes-textarea"
                        />
                    </SubSection>
                </Section>
            </div>
        );
    }
}

export default injectIntl(AddEditPanel);
